import React, { useRef, useState, useEffect } from 'react';
import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';

interface FileWithError {
  file: File;
  hasError: boolean;
  setShowUploadModal?: (value: boolean) => void;
}

interface UploadFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
  errorMessage?: string;
  failedFiles?: File[];
  setShowUploadModal?: (value: boolean) => void;
}

export const UploadFailedFilesModal: React.FC<UploadFilesModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  errorMessage,
  failedFiles = [],
  setShowUploadModal
}) => {
  const { t } = useTranslation();
  const [filesWithErrors, setFilesWithErrors] = useState<FileWithError[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (failedFiles.length > 0) {
      setFilesWithErrors(
        failedFiles.map(file => ({
          file,
          hasError: true
        }))
      );
    }
  }, [failedFiles]);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setFilesWithErrors(
        files.map(file => ({
          file,
          hasError: false
        }))
      );
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setFilesWithErrors(
        files.map(file => ({
          file,
          hasError: false
        }))
      );
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleTryAgainSingle = async (index: number) => {
    const fileToRetry = filesWithErrors[index].file;

    // Remove error state and start uploading
    const updatedFiles = [...filesWithErrors];
    updatedFiles[index].hasError = false;
    setFilesWithErrors(updatedFiles);

    // Trigger upload for this specific file
    onUpload([fileToRetry]);
    setShowUploadModal(false);

    // Remove the file from the list after initiating upload
    setTimeout(() => {
      setFilesWithErrors(prev => prev.filter((_, i) => i !== index));
    }, 500);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = filesWithErrors.filter((_, i) => i !== index);
    setFilesWithErrors(updatedFiles);

    // Close modal if no files left
    if (updatedFiles.length === 0) {
      onClose();
    }
  };

  const handleContinue = () => {
    const validFiles = filesWithErrors
      .filter(f => !f.hasError)
      .map(f => f.file);

    if (validFiles.length > 0) {
      onUpload(validFiles);
      setFilesWithErrors([]);
      onClose();
    }
  };

  const handleCancel = () => {
    setFilesWithErrors([]);
    onClose();
  };

  const hasAnyErrors = filesWithErrors.some(f => f.hasError);
  const hasValidFiles = filesWithErrors.some(f => !f.hasError);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[28rem] shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
          <h3 className="text-lg font-semibold">
            {t('dashboard.uploadFiles')}
          </h3>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            ×
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-4">
            <div className="flex justify-center mb-3">
              <div className="w-16 h-16 bg-[#E8F5E9] rounded-lg flex items-center justify-center">
                <SVGAssets.UploadModalIcon className="w-10 h-10 text-[#7F9D48]" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-800 mb-1">
              {t('dashboard.uploadYourFilesHere')}
            </p>
            <p className="text-xs text-gray-500 mb-3">
              {t('dashboard.dragImageHere')}
            </p>
            <button
              onClick={handleBrowse}
              className="text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center mx-auto gap-1">
              <span className="text-lg">⊕</span>
              <span>{t('dashboard.browseFileToUpload')}</span>
            </button>
          </div>

          {/* Global Error Message */}
          {hasAnyErrors && errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start justify-between">
              <div className="text-xs text-red-600 flex-1 whitespace-pre-line">
                {errorMessage}
              </div>
              <button
                onClick={() => setFilesWithErrors([])}
                className="text-red-400 hover:text-red-600 ml-2 text-lg leading-none">
                ×
              </button>
            </div>
          )}

          {/* Files List */}
          {filesWithErrors.map((fileWithError, index) => (
            <div
              key={index}
              className={`flex items-center justify-between rounded-lg p-3 mb-2 ${
                fileWithError.hasError
                  ? 'bg-white border border-red-200'
                  : 'bg-gray-50'
              }`}>
              <div className="flex items-center flex-1 min-w-0">
                <div
                  className={`w-10 h-10 rounded flex items-center justify-center mr-3 flex-shrink-0 ${
                    fileWithError.hasError ? 'bg-red-100' : 'bg-[#7F9D48]'
                  }`}>
                  <SVGAssets.UploadModalIcon
                    className={`w-6 h-6 ${
                      fileWithError.hasError ? 'text-red-500' : 'text-white'
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 flex items-center">
                    {fileWithError.hasError && (
                      <span className="text-red-500 mr-2 flex-shrink-0">
                        ⚠
                      </span>
                    )}
                    <span className="truncate">{fileWithError.file.name}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {(fileWithError.file.size / 1024).toFixed(0)} KB
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                {fileWithError.hasError && (
                  <button
                    onClick={() => handleTryAgainSingle(index)}
                    className="px-3 py-1.5 bg-[#7F9D48] text-white text-xs rounded hover:bg-[#6a8339] whitespace-nowrap">
                    {t('dashboard.tryAgain')}
                  </button>
                )}

                <SVGAssets.TrashIcon
                  className="w-4 h-4 ml-4"
                  onClick={() => handleRemoveFile(index)}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0">
          <Button
            btnStyle="link"
            className="px-6 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm"
            onClick={handleCancel}>
            {t('cancel')}
          </Button>

          <Button
            btnStyle="primary"
            type="submit"
            onClick={handleContinue}
            disabled={!hasValidFiles}
            className="px-6 rounded-lg py-3">
            {t('continue')}
          </Button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};
